import { BankAccountDto } from 'api/bank-account';
import { MandatePayer, MandatePayerOptionDto, QuoteMandateDto } from 'api/quote';
import { bankAccountsAPI, quotesAPI } from 'services/swagger-api';
import { createStoreWithReducer, shallow } from 'shared/store';

// Types
export interface QuoteMandateState {
  quoteMandate: QuoteMandateDto | null;
  /**
   * Comptes du payeur choisi dans le formulaire, rechargés à chaque changement de payeur : la
   * lecture du mandat ne renvoie que ceux du payeur enregistré. Le code du payeur accompagne la
   * liste pour ne jamais afficher les comptes d'un autre payeur pendant le rechargement.
   */
  payerBankAccounts: { payer: MandatePayer; bankAccounts: BankAccountDto[] } | null;
  loading: boolean;
  error: string | null;
}

type QuoteMandateAction =
  | { type: 'SET_QUOTE_MANDATE'; payload: QuoteMandateDto | null }
  | {
      type: 'SET_PAYER_BANK_ACCOUNTS';
      payload: { payer: MandatePayer; bankAccounts: BankAccountDto[] } | null;
    }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET' };

// Initial State
const initialState: QuoteMandateState = {
  quoteMandate: null,
  payerBankAccounts: null,
  loading: false,
  error: null
};

// Reducer
const quoteMandateReducer = (
  state: QuoteMandateState,
  action: QuoteMandateAction
): QuoteMandateState => {
  switch (action.type) {
    case 'SET_QUOTE_MANDATE':
      return { ...state, quoteMandate: action.payload };

    case 'SET_PAYER_BANK_ACCOUNTS':
      return { ...state, payerBankAccounts: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
};

let payerBankAccountsRequestId = 0;

// Store
export const useQuoteMandateStore = createStoreWithReducer<
  QuoteMandateState,
  QuoteMandateAction,
  {
    fetchQuoteMandate: (quoteId: string) => Promise<void>;
    fetchPayerBankAccounts: (payerOption: MandatePayerOptionDto) => Promise<void>;
    setQuoteMandate: (mandate: QuoteMandateDto | null) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    reset: () => void;
  }
>(quoteMandateReducer, initialState, (dispatch, getState) => ({
  fetchQuoteMandate: async (quoteId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await quotesAPI.getMandateByQuoteId({ quoteId });
      if (response) {
        dispatch({ type: 'SET_QUOTE_MANDATE', payload: response });
      }
    } catch (error) {
      console.error(
        'Erreur lors de la recuperation des informations de mandat:',
        error
      );
      dispatch({
        type: 'SET_ERROR',
        payload: 'Erreur lors de la recuperation du mandat'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  },

  fetchPayerBankAccounts: async (payerOption: MandatePayerOptionDto) => {
    // Seule la dernière demande compte : un changement rapide de payeur ne doit pas laisser
    // la réponse d'un payeur précédent écraser celle du payeur choisi.
    const requestId = ++payerBankAccountsRequestId;
    const payer = payerOption.code;
    try {
      // Cabinet du courtier connecté : ses comptes viennent de /comptes/courtier-connecte.
      const response =
        payer === MandatePayer.Courtier
          ? await bankAccountsAPI.getMyBankAccounts()
          : payerOption.personId
            ? await bankAccountsAPI.getBankAccountsByPersonId({ personId: payerOption.personId })
            : null;
      if (requestId !== payerBankAccountsRequestId) return;
      dispatch({
        type: 'SET_PAYER_BANK_ACCOUNTS',
        payload: { payer, bankAccounts: response?.bankAccounts ?? [] }
      });
    } catch (error) {
      console.error('Erreur lors de la recuperation des comptes du payeur:', error);
      if (requestId !== payerBankAccountsRequestId) return;
      dispatch({ type: 'SET_PAYER_BANK_ACCOUNTS', payload: { payer, bankAccounts: [] } });
    }
  },

  setQuoteMandate: (mandate: QuoteMandateDto | null) => {
    dispatch({ type: 'SET_QUOTE_MANDATE', payload: mandate });
  },

  setLoading: (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  },

  setError: (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  },

  reset: () => {
    payerBankAccountsRequestId++;
    dispatch({ type: 'RESET' });
  }
}));

// Selectors
export const useQuoteMandateData = () =>
  useQuoteMandateStore(state => state.quoteMandate);

export const useQuoteMandateLoading = () =>
  useQuoteMandateStore(state => state.loading);

export const useQuoteMandateError = () =>
  useQuoteMandateStore(state => state.error);

export const useQuoteMandateState = () =>
  useQuoteMandateStore(
    state => ({
      quoteMandate: state.quoteMandate,
      loading: state.loading,
      error: state.error
    }),
    shallow
  );

export const useQuoteMandateActions = () => useQuoteMandateStore.actions;

export const useQuoteMandateBankAccounts = () =>
  useQuoteMandateStore(state => state.quoteMandate?.bankAccounts ?? [], shallow);

export const usePayerBankAccounts = () =>
  useQuoteMandateStore(state => state.payerBankAccounts);

// Direct store access (for external usage)
export const quoteMandateStore = useQuoteMandateStore;
